import Keycloak from 'keycloak-js';

// Keycloak configuration
const keycloakConfig = {
  url: process.env.REACT_APP_KEYCLOAK_URL || 'https://auth.tools.kaptioapis.com',
  realm: 'kaptio',
  clientId: process.env.REACT_APP_KEYCLOAK_FRONTEND_CLIENT_ID || 'metric-mgmt-tool',
};

// Validate configuration
if (!keycloakConfig.clientId) {
  console.error('Keycloak clientId is required. Please set REACT_APP_KEYCLOAK_FRONTEND_CLIENT_ID environment variable.');
}

// Initialize Keycloak instance
const keycloak = new Keycloak(keycloakConfig);

// Track initialization state
let isInitialized = false;
let initPromise = null;

/**
 * Initialize Keycloak and return a promise that resolves when authentication is complete
 */
export const initKeycloak = () => {
  if (initPromise) {
    return initPromise;
  }
  
  initPromise = new Promise((resolve, reject) => {
    // Set a timeout to prevent hanging
    const timeout = setTimeout(() => {
      console.warn('Keycloak initialization timeout, resolving as unauthenticated');
      isInitialized = true;
      initPromise = null;
      resolve(false);
    }, 5000); // 5 second timeout
    
    keycloak
      .init({
        onLoad: 'check-sso', // Check SSO status on load
        // Removed silentCheckSsoRedirectUri - it causes CSP issues and we have checkLoginIframe: false
        pkceMethod: 'S256',
        checkLoginIframe: false, // Disable iframe check to avoid CSP issues
      })
      .then((authenticated) => {
        clearTimeout(timeout);
        isInitialized = true;
        if (authenticated) {
          console.log('User is authenticated');
          // Set up token refresh
          keycloak.onTokenExpired = () => {
            keycloak.updateToken(30).catch(() => {
              console.error('Failed to refresh token');
            });
          };
        } else {
          console.log('User is not authenticated');
        }
        initPromise = null;
        resolve(authenticated);
      })
      .catch((error) => {
        clearTimeout(timeout);
        console.error('Keycloak initialization failed', error);
        // Don't reject - resolve as unauthenticated so the app can continue
        // This allows users to click login even if init fails
        isInitialized = true; // Mark as initialized so login() can work
        initPromise = null;
        resolve(false);
      });
  });
  
  return initPromise;
};

/**
 * Login function - redirects to Keycloak login page
 * Ensures Keycloak is initialized before attempting login
 */
export const login = () => {
  if (!keycloakConfig.clientId) {
    console.error('Keycloak clientId is not configured');
    return;
  }
  
  // Get the redirect URI - use current origin
  const redirectUri = window.location.origin + window.location.pathname;
  
  // If not initialized yet, initialize first, then login
  if (!isInitialized && !initPromise) {
    initKeycloak()
      .then(() => {
        // Now that it's initialized, call login with explicit redirect URI
        keycloak.login({
          redirectUri: redirectUri
        });
      })
      .catch((error) => {
        console.error('Failed to initialize Keycloak', error);
        // Try to login anyway - might work if adapter is partially ready
        try {
          keycloak.login({
            redirectUri: redirectUri
          });
        } catch (loginError) {
          console.error('Login failed', loginError);
        }
      });
  } else if (initPromise) {
    // Initialization in progress, wait for it
    initPromise
      .then(() => {
        keycloak.login({
          redirectUri: redirectUri
        });
      })
      .catch((error) => {
        console.error('Initialization failed, trying login anyway', error);
        try {
          keycloak.login({
            redirectUri: redirectUri
          });
        } catch (loginError) {
          console.error('Login failed', loginError);
        }
      });
  } else {
    // Already initialized, just call login with explicit redirect URI
    try {
      keycloak.login({
        redirectUri: redirectUri
      });
    } catch (error) {
      console.error('Login failed', error);
    }
  }
};

/**
 * Logout function
 */
export const logout = () => {
  keycloak.logout();
};

/**
 * Get the current access token
 */
export const getToken = () => {
  return keycloak.token;
};

/**
 * Check if user is authenticated
 */
export const isAuthenticated = () => {
  return keycloak.authenticated;
};

/**
 * Get user info from token
 */
export const getUserInfo = () => {
  if (keycloak.tokenParsed) {
    return {
      username: keycloak.tokenParsed.preferred_username,
      email: keycloak.tokenParsed.email,
      name: keycloak.tokenParsed.name,
      sub: keycloak.tokenParsed.sub,
    };
  }
  return null;
};

/**
 * Update token if needed
 */
export const updateToken = () => {
  return keycloak.updateToken(30);
};

export default keycloak;

